import { getCustomerId } from '../../../utils/cookies';
import { getLoggedInUserMatchKeys } from '../../../utils/userDisplay';
import { getAllRequestsByCustomerID } from './api';
import type { RequestTrackingItem, RequestTrackingStatus } from './types';

export const MS_PROJECT_WBS_SETUP_REQUEST_TYPE = 'MS_PROJECT_WBS_SETUP';

export function isWbsSetupRequestInProgress(status: RequestTrackingStatus): boolean {
  return status === 'queued' || status === 'running';
}

export function requestTypeIsWbsSetup(requestType: string | null | undefined): boolean {
  return String(requestType ?? '')
    .trim()
    .toUpperCase() === MS_PROJECT_WBS_SETUP_REQUEST_TYPE;
}

function tokenizeUserIdentity(value: string): string[] {
  return value
    .split(/[\s@._-]+/)
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part.length >= 2);
}

function identityStringsMatch(request: string, key: string): boolean {
  if (request === key || request.includes(key) || key.includes(request)) {
    return true;
  }
  const requestParts = tokenizeUserIdentity(request);
  const keyParts = tokenizeUserIdentity(key);
  return requestParts.some((rp) =>
    keyParts.some((kp) => rp === kp || rp.includes(kp) || kp.includes(rp)),
  );
}

/** Match tracker `user` / `userName` to the current session identity keys. */
export function requestBelongsToUser(
  requestUser: string | null | undefined,
  matchKeys: string[],
): boolean {
  const request = String(requestUser ?? '').trim().toLowerCase();
  if (!request) {
    return matchKeys.length === 0;
  }
  if (matchKeys.length === 0) {
    return true;
  }
  return matchKeys.some((key) => identityStringsMatch(request, key));
}

/** True when a WBS setup request is still active (queued/running or not yet completed). */
export function isWbsSetupRequestActive(item: RequestTrackingItem): boolean {
  const type = item.requestType ?? item.source;
  if (!requestTypeIsWbsSetup(type)) {
    return false;
  }
  if (isWbsSetupRequestInProgress(item.status)) {
    return true;
  }
  const completedTime = String(item.requestCompletedTime ?? item.completedAt ?? '').trim();
  if (completedTime) {
    return false;
  }
  return item.status !== 'completed' && item.status !== 'failed' && item.status !== 'cancelled';
}

export function isActiveWbsSetupImportForUser(
  items: RequestTrackingItem[],
  matchKeys: string[] = getLoggedInUserMatchKeys(),
): boolean {
  return items.some(
    (item) => isWbsSetupRequestActive(item) && requestBelongsToUser(item.userName, matchKeys),
  );
}

export async function fetchActiveWbsSetupImportForCurrentUser(): Promise<{
  blocked: boolean;
  activeRequest: RequestTrackingItem | null;
}> {
  const matchKeys = getLoggedInUserMatchKeys();
  const items = await getAllRequestsByCustomerID(getCustomerId());
  const activeRequest =
    items.find(
      (item) => isWbsSetupRequestActive(item) && requestBelongsToUser(item.userName, matchKeys),
    ) ?? null;

  return { blocked: activeRequest != null, activeRequest };
}
